import React from 'react';
import { Link } from 'react-router-dom';
import ImageBanner from './../assests/WhatsApp-API-Features.png';
const featureCards = [
  ['WhatsApp Messaging API', 'Integrate WhatsApp messaging into your website, application, or business workflow.'],
  ['WhatsApp Automation', 'Reduce manual work with automated customer notifications, reminders, and updates.'],
  ['WhatsApp Chatbots and AI', 'Build customer experiences using supported automation and business logic.'],
  ['WhatsApp Campaigns', 'Manage approved messaging flows and eligible customer outreach.'],
  ['WhatsApp CRM Integration', 'Connect customer records and follow-up workflows with WhatsApp conversations.'],
  ['Developer-Friendly REST API', 'Use documented endpoints to connect your backend and build custom integrations.'],
];

const businessTypes = ['Startups', 'Developers', 'SaaS Companies', 'Digital Marketing Agencies', 'E-commerce Businesses', 'Service Businesses'];
const workflowSteps = [
  'Create Your Account',
  'Connect Your WhatsApp Business Account',
  'Configure Your API Integration',
  'Start Building and Automating',
];
const integrationUseCases = ['Node.js and Express.js applications', 'React and Next.js applications', 'PHP and Laravel applications', 'CRM and customer support systems', 'E-commerce and order management platforms', 'AI assistants and chatbot workflows', 'Webhooks and event-driven applications'];
const reasons = [
  ['No recurring platform subscription', 'Reduce fixed software costs when launching WhatsApp communication.'],
  ['API-first approach', 'Connect WhatsApp with your existing backend, website, CRM, and business applications.'],
  ['Automation-ready workflows', 'Reduce repetitive tasks and improve customer communication efficiency.'],
  ['Flexible integration opportunities', 'Build custom experiences for your business or eligible customer use cases.'],
];

const pricingRows = [
  ['Hawktrion platform subscription', '₹0 under the free offer'],
  ['API integration', 'Depends on your implementation needs'],
  ['Meta WhatsApp messaging', 'Applicable Meta rates may apply'],
  ['Taxes', 'Applicable where required'],
  ['Optional third-party services', 'Disclosed separately where applicable'],
];

const PageIntroList = ({ items }: { items: string[] }) => (
  <ul className="business-api-list">
    {items.map((item) => <li key={item}>{item}</li>)}
  </ul>
);

const CardGrid = ({ items }: { items: string[][] }) => (
  <div className="business-api-grid">
    {items.map(([title, description]) => (
      <article className="business-api-card" key={title}>
        <h3>{title}</h3>
        <p>{description}</p>
      </article>
    ))}
  </div>
);

const NoPlatformChargeContent = () => (
  <div className="business-api-content">
    <section className="business-api-section">
      <div className="container">
        <div className="business-api-heading">
          <div className="sec-label"><span className="sec-label-dot"></span>Free WhatsApp API Access</div>
          <h2>Get WhatsApp Business API Access Without Platform Subscription Fees</h2>
          <p className="business-api-lead">Power your business with WhatsApp. Not expensive software subscriptions.</p>
        </div>

        <div className="business-api-intro-grid">
          <div className="business-api-intro-copy">
            <p>Access WhatsApp Business API capabilities with zero Hawktrion platform fees. Send messages, automate customer communication, build chatbots, manage campaigns, and integrate WhatsApp into your applications without paying a recurring platform subscription.</p>
            <p>Whether you're a startup, developer, agency, SaaS provider, or growing business, Hawktrion gives you a simpler way to build and scale WhatsApp-powered experiences.</p>
            <PageIntroList items={[
              'Zero monthly Hawktrion platform subscription fees',
              'Developer-friendly API integration',
              'Automated customer notifications and workflows',
              'Campaigns and approved message templates',
              'Built for businesses, agencies, and SaaS platforms',
            ]} />
            <div className="business-api-actions">
              <Link to="/signup" className="btn btn-p">Get Free API Access</Link>
              <a href="https://docs-waba.hawktrion.com/" target="_blank" rel="noreferrer" className="btn btn-s">Explore API Documentation</a>
            </div>
            <p className="business-api-copy">Hawktrion platform access is free under the applicable offer. Meta WhatsApp messaging charges, taxes, and any separately disclosed third-party costs may apply. WhatsApp Business Account eligibility and Meta policies apply.</p>
          </div>

          <div className="business-api-visual">
            <img src={ImageBanner} alt="WhatsApp API features illustration for business messaging and automation" />
            <figcaption>Build chat experiences, automate customer communication, and keep messaging costs transparent.</figcaption>
          </div>
        </div>
      </div>
    </section>

    <section className="business-api-section">
      <div className="container">
        <div className="business-api-heading">
          <div className="sec-label"><span className="sec-label-dot"></span>Why It Matters</div>
          <h2>WhatsApp API Features for Businesses and Developers</h2>
        </div>
        <p className="business-api-lead">Powerful WhatsApp capabilities shouldn't require an expensive software subscription.</p>
        <p className="business-api-copy">Many businesses want to automate customer communication but don't want another monthly platform bill. Hawktrion aims to remove that barrier by offering WhatsApp API access without a recurring Hawktrion platform subscription.</p>
        <p className="business-api-copy">Use the platform to build customer engagement workflows, connect your existing systems, and automate repetitive communication while keeping your messaging costs transparent.</p>
        <h3 className="business-api-subheading">With Hawktrion, you can:</h3>
        <PageIntroList items={[
          'Connect your business to the WhatsApp Business Platform.',
          'Send approved WhatsApp template messages.',
          'Automate transactional notifications and customer updates.',
          'Build chatbots and integrate AI-powered workflows.',
          'Connect WhatsApp with your CRM, website, or application.',
          'Develop your own WhatsApp-powered SaaS products.',
        ]} />
        <div className="business-api-actions">
          <Link to="/signup" className="btn btn-p">Start building without a recurring platform subscription.</Link>
        </div>
      </div>
    </section>

    <section className="business-api-section">
      <div className="container">
        <div className="business-api-heading">
          <div className="sec-label"><span className="sec-label-dot"></span>Platform Features</div>
          <h2>WhatsApp Automation, Chatbots, and CRM Integration</h2>
        </div>
        <CardGrid items={featureCards} />
      </div>
    </section>

    <section className="business-api-section">
      <div className="container">
        <div className="business-api-heading">
          <div className="sec-label"><span className="sec-label-dot"></span>Who It's For</div>
          <h2>Free WhatsApp API for SaaS Companies and Agencies</h2>
        </div>
        <div className="business-api-grid">
          {businessTypes.map((item) => (
            <article className="business-api-card" key={item}>
              <h3>{item}</h3>
              <p>{
                item === 'Startups' ? 'Launch WhatsApp-powered customer communication without adding a recurring platform subscription to your initial software costs.' :
                item === 'Developers' ? 'Build, test, and integrate WhatsApp messaging into websites, mobile applications, and backend services using API-based workflows.' :
                item === 'SaaS Companies' ? 'Explore WhatsApp functionality for your software product and customer workflows.' :
                item === 'Digital Marketing Agencies' ? 'Manage eligible client messaging workflows, customer notifications, and approved campaigns while keeping platform subscription expenses under control.' :
                item === 'E-commerce Businesses' ? 'Automate order updates, customer support, delivery notifications, and other relevant shopping experiences.' :
                'Simplify appointment confirmations, reminders, lead follow-ups, and customer communication.'
              }</p>
            </article>
          ))}
        </div>
      </div>
    </section>

    <section className="business-api-section">
      <div className="container">
        <div className="business-api-heading">
          <div className="sec-label"><span className="sec-label-dot"></span>Implementation</div>
          <h2>How to Integrate the WhatsApp Business API</h2>
        </div>
        <h3 className="business-api-subheading">Start Using WhatsApp Business API in Four Steps</h3>
        <div className="business-api-grid">
          {workflowSteps.map((step, index) => (
            <article className="business-api-card" key={step}>
              <h3>Step {index + 1}: {step}</h3>
              <p>{
                index === 0 ? 'Register with Hawktrion and review the requirements for free platform access.' :
                index === 1 ? 'Complete the required Meta onboarding and permissions, including business verification where applicable.' :
                index === 2 ? 'Set up your credentials, supported endpoints, webhooks, and approved message templates.' :
                'Integrate WhatsApp into your application and start using the features available to your account.'
              }</p>
            </article>
          ))}
        </div>
        <div className="business-api-actions">
          <Link to="/signup" className="btn btn-p">Get Started for Free</Link>
        </div>
        <p className="business-api-copy">Onboarding time depends on Meta approval, account eligibility, number registration, and integration requirements.</p>
      </div>
    </section>

    <section className="business-api-section">
      <div className="container">
        <div className="business-api-heading">
          <div className="sec-label"><span className="sec-label-dot"></span>Developer Support</div>
          <h2>Built for Developers and Modern Applications</h2>
        </div>
        <p className="business-api-lead">Integrate WhatsApp into your existing technology stack.</p>
        <p className="business-api-copy">Hawktrion helps developers connect WhatsApp communication with their applications and backend services.</p>
        <PageIntroList items={integrationUseCases} />
        <p className="business-api-copy">Use API-based messaging to connect customer communication with the systems your business already relies on.</p>
        <div className="business-api-actions">
          <a href="https://docs-waba.hawktrion.com/" target="_blank" rel="noreferrer" className="btn btn-s">View Developer Documentation</a>
        </div>
      </div>
    </section>

    <section className="business-api-section">
      <div className="container">
        <div className="business-api-heading">
          <div className="sec-label"><span className="sec-label-dot"></span>Pricing</div>
          <h2>WhatsApp Business API Pricing and Messaging Charges</h2>
        </div>
        <p className="business-api-lead">No recurring platform subscription under the free offer.</p>
        <div className="business-api-card" style={{ marginBottom: '18px' }}>
          <div style={{ display: 'grid', gap: '10px' }}>
            {pricingRows.map(([label, value]) => (
              <div key={label} style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', borderBottom: '1px solid var(--border)', paddingBottom: '10px' }}>
                <strong style={{ color: 'var(--white)' }}>{label}</strong>
                <span style={{ color: 'var(--muted)' }}>{value}</span>
              </div>
            ))}
          </div>
        </div>
        <p className="business-api-copy">Free platform access does not mean unlimited free WhatsApp messages. Meta determines messaging charges, eligibility, usage limits, and applicable policies. Check the current Meta pricing information before launching campaigns.</p>
        <p className="business-api-copy">Our goal is to help businesses reduce software overhead while maintaining clear visibility into messaging costs.</p>
        <div className="business-api-actions">
          <Link to="/signup" className="btn btn-p">Get Free API Access</Link>
        </div>
      </div>
    </section>

    <section className="business-api-section">
      <div className="container">
        <div className="business-api-heading">
          <div className="sec-label"><span className="sec-label-dot"></span>FAQ</div>
          <h2>Frequently Asked Questions About Free WhatsApp API Access</h2>
        </div>
        <div className="business-api-grid">
          {reasons.map(([title, description]) => (
            <article className="business-api-card" key={title}>
              <h3>{title}</h3>
              <p>{description}</p>
            </article>
          ))}
        </div>
      </div>
    </section>
  </div>
);

export default NoPlatformChargeContent;
